import { useLayoutEffect, useRef, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { MAIN_STEPS, type StepId, type StepInfo } from '../../lib/preflightSteps';
import { prefersReducedMotion } from './hooks';

/** The ready count rolls up or down like an odometer when it changes. */
export function RollingNumber({ n }: { n: number }) {
  const [pair, setPair] = useState<{ from: number; to: number } | null>(null);
  const prev = useRef(n);
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (prev.current === n) return;
    const from = prev.current;
    prev.current = n;
    setPair(prefersReducedMotion() ? null : { from, to: n });
  }, [n]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!pair || !el) return;
    const up = pair.to > pair.from;
    const kids = [...el.children] as HTMLElement[];
    for (const k of kids) {
      k.style.transition = 'none';
      k.style.transform = up ? 'translateY(0)' : 'translateY(-100%)';
    }
    void el.offsetWidth;
    for (const k of kids) {
      k.style.transition = '';
      k.style.transform = up ? 'translateY(-100%)' : 'translateY(0)';
    }
    const t = window.setTimeout(() => setPair(null), 500);
    return () => window.clearTimeout(t);
  }, [pair]);

  let digits: number[] = [n];
  if (pair) digits = pair.to > pair.from ? [pair.from, pair.to] : [pair.to, pair.from];
  return (
    <span className="pf-roll pf-num" ref={ref} aria-hidden="true">
      {digits.map((d, i) => (
        <span key={`${pair ? 'p' : 's'}-${i}-${d}`}>{d}</span>
      ))}
    </span>
  );
}

export function FlightCard({
  steps,
  ready,
  next,
  sendLine = null,
  onNext,
}: {
  steps: Record<StepId, StepInfo>;
  ready: number;
  next: { label: string; sub: string };
  /** When the campaign goes out ("Sends Thu 8 Oct 2026, 09:00 (Europe/Berlin)."), once the time is valid. */
  sendLine?: string | null;
  onNext: () => void;
}) {
  return (
    <section className="pf-flight" aria-label="Progress">
      <div className="pf-flight-top">
        <div className="pf-ready">
          <RollingNumber n={ready} />
          <span className="pf-of" aria-hidden="true">
            of 5 ready
          </span>
          <span className="pf-sr" aria-live="polite">
            {ready} of 5 ready
          </span>
        </div>
        <button type="button" className="pf-next" onClick={onNext}>
          <span>{next.label}</span>
          <ArrowDown aria-hidden="true" />
        </button>
      </div>
      <div className="pf-segs" aria-hidden="true">
        {MAIN_STEPS.map((id) => (
          <span key={id} className="pf-seg" data-s={steps[id].state} />
        ))}
      </div>
      <p className="pf-flight-sub">{next.sub}</p>
      {sendLine ? <p className="pf-flight-sub pf-flight-send">{sendLine}</p> : null}
    </section>
  );
}
